// src/components/admin/blog/BlogEditorModal.jsx - Modified for Server-Side OG
// Social previews are for visual reference only - actual OG tags are generated server-side

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FiXCircle,
  FiEdit2,
  FiTrendingUp,
  FiBarChart2,
  FiImage,
  FiSave,
  FiClock,
  FiCheckCircle,
  FiAlertCircle,
  FiUploadCloud,
  FiLink,
  FiTwitter,
  FiFacebook,
  FiEye,
  FiRefreshCw,
  FiCopy,
  FiCheck,
  FiX,
  FiInfo,
  FiShare2,
  FiGrid,
  FiInstagram,
  FiLinkedin,
  FiMessageCircle,
  FiSend,
  FiZap,
} from 'react-icons/fi';
import { BlogEditor } from './BlogEditor';
import { ImageUploader } from './ImageUploader';
import { SlugGenerator } from './SlugGenerator';
import { SEOScoreHelper } from './SEOScoreHelper';
import { CategoryManager } from './CategoryManager';
import { TagManager } from './TagManager';
import { SchedulePicker } from './SchedulePicker';
import BlogAnalytics from './BlogAnalytics';
import { useTheme } from '../../../contexts/ThemeContext';
import { useToast } from '../../../hooks/useToast';

// ✅ REMOVED: generateSocialImageUrl, generateAllSocialImages, SOCIAL_PLATFORMS
// These are no longer needed for OG generation - server handles it
// import {
//   generateSocialImageUrl,
//   generateAllSocialImages,
//   SOCIAL_PLATFORMS,
// } from '../../../services/cloudinarySocialService';

// =============================================
// SOCIAL SHARING PREVIEW COMPONENT (UI ONLY)
// This is for visual preview in the admin panel only.
// Actual OG tags are generated server-side via API.
// =============================================

const SocialSharingPreview = ({ blogData, imagePreview }) => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const [selectedPlatform, setSelectedPlatform] = useState('facebook');

  // Platform configurations for preview UI only
  const SOCIAL_PLATFORMS = {
    FACEBOOK: {
      id: 'facebook',
      name: 'Facebook',
      width: 1200,
      height: 630,
      ratio: '1.91:1',
      maxTitleLength: 90,
      maxDescriptionLength: 200,
      color: '#1877F2',
    },
    TWITTER: {
      id: 'twitter',
      name: 'Twitter',
      width: 1200,
      height: 675,
      ratio: '16:9',
      maxTitleLength: 90,
      maxDescriptionLength: 200,
      color: '#1DA1F2',
    },
    LINKEDIN: {
      id: 'linkedin',
      name: 'LinkedIn',
      width: 1200,
      height: 627,
      ratio: '1.91:1',
      maxTitleLength: 90,
      maxDescriptionLength: 200,
      color: '#0A66C2',
    },
    WHATSAPP: {
      id: 'whatsapp',
      name: 'WhatsApp',
      width: 800,
      height: 418,
      ratio: '1.91:1',
      maxTitleLength: 60,
      maxDescriptionLength: 150,
      color: '#25D366',
    },
    TELEGRAM: {
      id: 'telegram',
      name: 'Telegram',
      width: 800,
      height: 418,
      ratio: '1.91:1',
      maxTitleLength: 60,
      maxDescriptionLength: 150,
      color: '#26A5E4',
    },
    INSTAGRAM: {
      id: 'instagram',
      name: 'Instagram',
      width: 1080,
      height: 1080,
      ratio: '1:1',
      maxTitleLength: 40,
      maxDescriptionLength: 120,
      color: '#E4405F',
    },
  };

  // Get all platform previews with proper dimensions
  const platformPreviews = useMemo(() => {
    const imageUrl = blogData.ogImage || imagePreview;
    const title =
      blogData.ogTitle || blogData.seoTitle || blogData.title || 'Blog Post';

    const previews = {};

    Object.keys(SOCIAL_PLATFORMS).forEach(key => {
      const platform = SOCIAL_PLATFORMS[key];
      const platformId = platform.id;

      // For preview, just use the image URL as is (no transformation needed)
      // The server will generate the actual OG images
      const platformImage = imageUrl || null;

      previews[platformId] = {
        ...platform,
        image: platformImage,
        title:
          title.length > platform.maxTitleLength
            ? title.substring(0, platform.maxTitleLength - 3) + '...'
            : title,
        description:
          blogData.ogDescription ||
          blogData.seoDescription ||
          blogData.excerpt ||
          '',
      };
    });

    return previews;
  }, [blogData, imagePreview]);

  const currentPreview =
    platformPreviews[selectedPlatform] || platformPreviews.facebook;

  // Get platform icon
  const getPlatformIcon = platformId => {
    const icons = {
      facebook: FiFacebook,
      twitter: FiTwitter,
      linkedin: FiLinkedin,
      whatsapp: FiMessageCircle,
      telegram: FiSend,
      instagram: FiInstagram,
    };
    return icons[platformId] || FiShare2;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3
          className="text-sm font-semibold flex items-center gap-2"
          style={{ color: isDark ? '#ffffff' : '#0a0a0a' }}
        >
          <FiShare2 className="w-4 h-4" />
          Social Sharing Previews
          <span
            className="text-[10px] font-normal"
            style={{
              color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
            }}
          >
            (Visual preview only - OG tags generated server-side)
          </span>
        </h3>
        <span
          className="text-[10px]"
          style={{
            color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
          }}
        >
          Click platform icons to preview
        </span>
      </div>

      {/* Platform Selector */}
      <div className="flex flex-wrap gap-1.5">
        {Object.keys(SOCIAL_PLATFORMS).map(key => {
          const platform = SOCIAL_PLATFORMS[key];
          const isSelected = selectedPlatform === platform.id;
          const Icon = getPlatformIcon(platform.id);

          return (
            <button
              key={platform.id}
              onClick={() => setSelectedPlatform(platform.id)}
              className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 flex items-center gap-1.5 ${
                isSelected
                  ? 'bg-black text-white dark:bg-white dark:text-black shadow-md'
                  : 'border hover:bg-black/5 dark:hover:bg-white/5'
              }`}
              style={{
                borderColor: isDark
                  ? 'rgba(255,255,255,0.1)'
                  : 'rgba(0,0,0,0.1)',
              }}
            >
              <Icon className="w-3.5 h-3.5" />
              <span>{platform.name}</span>
              <span className="text-[9px] opacity-50">
                ({platform.width}×{platform.height})
              </span>
            </button>
          );
        })}
      </div>

      {/* Preview Card */}
      <div
        className="rounded-xl border overflow-hidden transition-all"
        style={{
          borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
        }}
      >
        {/* Platform Header */}
        <div
          className="px-4 py-2 flex items-center justify-between border-b"
          style={{
            borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
          }}
        >
          <div className="flex items-center gap-2">
            <div
              className="w-7 h-7 rounded-lg flex items-center justify-center"
              style={{
                backgroundColor: `${currentPreview.color}15`,
                color: currentPreview.color,
              }}
            >
              {React.createElement(getPlatformIcon(currentPreview.id), {
                className: 'w-3.5 h-3.5',
              })}
            </div>
            <span
              className="text-sm font-medium"
              style={{ color: isDark ? '#ffffff' : '#0a0a0a' }}
            >
              {currentPreview.name}
            </span>
          </div>
          <span
            className="text-[10px]"
            style={{
              color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
            }}
          >
            {currentPreview.width}×{currentPreview.height} (
            {currentPreview.ratio})
          </span>
        </div>

        {/* Preview Content */}
        <div className="p-3">
          {currentPreview.image ? (
            <div
              className="relative rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800"
              style={{
                aspectRatio: `${currentPreview.width}/${currentPreview.height}`,
              }}
            >
              <img
                src={currentPreview.image}
                alt={`${currentPreview.name} preview`}
                className="w-full h-full object-cover"
                loading="lazy"
              />
              {/* Text Overlay for platforms that support it */}
              {(selectedPlatform === 'facebook' ||
                selectedPlatform === 'twitter' ||
                selectedPlatform === 'linkedin') && (
                <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 via-black/50 to-transparent">
                  <p className="text-white font-semibold text-sm line-clamp-2">
                    {currentPreview.title}
                  </p>
                  {currentPreview.description && (
                    <p className="text-white/70 text-xs line-clamp-2 mt-0.5">
                      {currentPreview.description.length >
                      currentPreview.maxDescriptionLength
                        ? currentPreview.description.substring(
                            0,
                            currentPreview.maxDescriptionLength - 3,
                          ) + '...'
                        : currentPreview.description}
                    </p>
                  )}
                  <p className="text-white/40 text-[10px] mt-0.5">
                    {blogData.canonicalUrl ||
                      `https://wdhejbul.com/blog/${blogData.slug || 'post'}`}
                  </p>
                </div>
              )}
              {/* Platform-specific badge */}
              <div className="absolute top-3 right-3 px-2 py-0.5 rounded-md text-[10px] font-medium bg-black/60 text-white backdrop-blur-sm">
                {currentPreview.name}
              </div>
            </div>
          ) : (
            <div
              className="rounded-lg bg-gray-100 dark:bg-gray-800 flex flex-col items-center justify-center p-8"
              style={{
                aspectRatio: `${currentPreview.width}/${currentPreview.height}`,
              }}
            >
              {React.createElement(FiShare2, {
                className: 'w-12 h-12 opacity-30',
                style: { color: isDark ? '#ffffff' : '#0a0a0a' },
              })}
              <p
                className="text-sm mt-3 opacity-50"
                style={{ color: isDark ? '#ffffff' : '#0a0a0a' }}
              >
                No image selected
              </p>
              <p
                className="text-xs opacity-30"
                style={{ color: isDark ? '#ffffff' : '#0a0a0a' }}
              >
                Upload a featured image or set an OG image
              </p>
            </div>
          )}

          {/* Preview Details */}
          <div className="mt-3 space-y-1">
            <p
              className="text-sm font-medium truncate"
              style={{ color: isDark ? '#ffffff' : '#0a0a0a' }}
            >
              {currentPreview.title}
            </p>
            {currentPreview.description && (
              <p
                className="text-xs truncate"
                style={{
                  color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                }}
              >
                {currentPreview.description}
              </p>
            )}
            <p
              className="text-[10px] truncate"
              style={{
                color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
              }}
            >
              {blogData.canonicalUrl ||
                `https://wdhejbul.com/blog/${blogData.slug || 'post'}`}
            </p>
          </div>
        </div>
      </div>

      {/* Note about server-side OG generation */}
      <div
        className="p-3 rounded-lg text-[10px]"
        style={{
          backgroundColor: isDark
            ? 'rgba(59, 130, 246, 0.1)'
            : 'rgba(59, 130, 246, 0.05)',
          border: '1px solid rgba(59, 130, 246, 0.15)',
        }}
      >
        <div className="flex items-center gap-2">
          <FiInfo className="w-3.5 h-3.5 text-blue-500" />
          <span
            style={{
              color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)',
            }}
          >
            This is a visual preview only. Actual OG tags are generated
            server-side when the post is shared. The server uses your featured
            image and SEO settings to create optimized social sharing images.
          </span>
        </div>
      </div>
    </div>
  );
};

// =============================================
// MAIN BLOG EDITOR MODAL
// =============================================

const BlogEditorModal = ({
  isOpen,
  mode,
  initialData,
  categories,
  tags,
  isSubmitting: externalIsSubmitting,
  onClose,
  onSubmit,
  onCategoriesUpdate,
  currentBlog,
  onRefresh,
}) => {
  const { resolvedTheme } = useTheme();
  const { showToast } = useToast();

  const [activeTab, setActiveTab] = useState('editor');
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    category: '',
    tags: [],
    status: 'draft',
    featured: false,
    scheduledAt: null,
    seoTitle: '',
    seoDescription: '',
    seoKeywords: [],
    canonicalUrl: '',
    ogImage: '',
    ogTitle: '',
    ogDescription: '',
    twitterTitle: '',
    twitterDescription: '',
    twitterImage: '',
    imageAlt: '',
    imageCaption: '',
    slug: '',
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [editorPendingImages, setEditorPendingImages] = useState([]);
  const [imagesReadyForSubmit, setImagesReadyForSubmit] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [showAutoSaveIndicator, setShowAutoSaveIndicator] = useState(false);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [generatedOgImage, setGeneratedOgImage] = useState('');
  const [generatedTwitterImage, setGeneratedTwitterImage] = useState('');
  const [autoSaveTimer, setAutoSaveTimer] = useState(null);
  const [isAutoFilling, setIsAutoFilling] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // SEO validation states
  const [seoValidation, setSeoValidation] = useState({
    seoTitle: { valid: false, message: '' },
    seoDescription: { valid: false, message: '' },
    seoKeywords: { valid: false, message: '' },
    ogTitle: { valid: false, message: '' },
    ogDescription: { valid: false, message: '' },
    ogImage: { valid: false, message: '' },
  });

  // Load initial data
  useEffect(() => {
    if (isOpen && mode === 'edit' && initialData) {
      setFormData({
        title: initialData.title || '',
        excerpt: initialData.excerpt || '',
        content: initialData.content || '',
        category: initialData.category || '',
        tags: initialData.tags || [],
        status: initialData.status || 'draft',
        featured: initialData.featured || false,
        scheduledAt: initialData.scheduledAt || null,
        seoTitle: initialData.seoTitle || '',
        seoDescription: initialData.seoDescription || '',
        seoKeywords: initialData.seoKeywords || [],
        canonicalUrl: initialData.canonicalUrl || '',
        ogImage: initialData.ogImage || '',
        ogTitle: initialData.ogTitle || '',
        ogDescription: initialData.ogDescription || '',
        twitterTitle: initialData.twitterTitle || '',
        twitterDescription: initialData.twitterDescription || '',
        twitterImage: initialData.twitterImage || '',
        imageAlt: initialData.imageAlt || '',
        imageCaption: initialData.imageCaption || '',
        slug: initialData.slug || '',
      });
      setImagePreview(initialData.featuredImage || '');

      if (initialData.featuredImage && !initialData.ogImage) {
        autoGenerateSocialFields(initialData);
      }
      setActiveTab('editor');
    } else if (isOpen && mode === 'create') {
      resetForm();
      setActiveTab('editor');
    }
  }, [isOpen, mode, initialData]);

  // Validate SEO fields on change
  useEffect(() => {
    validateSEOFields();
  }, [
    formData.seoTitle,
    formData.seoDescription,
    formData.seoKeywords,
    formData.ogTitle,
    formData.ogDescription,
    formData.ogImage,
  ]);

  const resetForm = () => {
    setFormData({
      title: '',
      excerpt: '',
      content: '',
      category: '',
      tags: [],
      status: 'draft',
      featured: false,
      scheduledAt: null,
      seoTitle: '',
      seoDescription: '',
      seoKeywords: [],
      canonicalUrl: '',
      ogImage: '',
      ogTitle: '',
      ogDescription: '',
      twitterTitle: '',
      twitterDescription: '',
      twitterImage: '',
      imageAlt: '',
      imageCaption: '',
      slug: '',
    });
    setImageFile(null);
    setImagePreview('');
    setEditorPendingImages([]);
    setImagesReadyForSubmit(false);
    setGeneratedOgImage('');
    setGeneratedTwitterImage('');
    setSubmitError(null);
  };

  // Generate social images from featured image using Cloudinary transformations
  // This is for the admin preview only - server will generate final OG images
  const generateSocialImages = (featuredImageUrl, title) => {
    if (!featuredImageUrl) return { ogImage: '', twitterImage: '' };

    try {
      // Check if it's a Cloudinary URL
      if (featuredImageUrl.includes('cloudinary.com')) {
        const baseUrl = featuredImageUrl.split('/upload/')[0];
        const imagePath = featuredImageUrl.split('/upload/')[1];

        if (baseUrl && imagePath) {
          // Generate platform-specific images for preview
          const ogImage = `${baseUrl}/upload/c_fill,w_1200,h_630,g_auto,q_auto:good/${imagePath}`;
          const twitterImage = `${baseUrl}/upload/c_fill,w_1200,h_675,g_auto,q_auto:good/${imagePath}`;
          return { ogImage, twitterImage };
        }
      }

      // For non-Cloudinary images, use as-is (for preview)
      return { ogImage: featuredImageUrl, twitterImage: featuredImageUrl };
    } catch (error) {
      console.error('Error generating social images:', error);
      return { ogImage: '', twitterImage: '' };
    }
  };

  // Auto-generate social fields from blog data (for admin preview only)
  const autoGenerateSocialFields = useCallback(
    data => {
      const title = data?.title || formData.title || '';
      const excerpt = data?.excerpt || formData.excerpt || '';
      const content = data?.content || formData.content || '';
      const tags = data?.tags || formData.tags || [];
      const featuredImage =
        data?.featuredImage || data?.imagePreview || imagePreview || '';

      setIsAutoFilling(true);

      const updates = {};

      // SEO Title
      if (!formData.seoTitle && title) {
        updates.seoTitle =
          title.length > 60
            ? title.substring(0, 57) + '... - WD Hejbul'
            : title + ' - WD Hejbul';
      }

      // SEO Description
      if (!formData.seoDescription) {
        const desc =
          excerpt || content.replace(/<[^>]*>/g, '').substring(0, 160);
        if (desc) {
          updates.seoDescription =
            desc.length > 160 ? desc.substring(0, 157) + '...' : desc;
        }
      }

      // OG Title (max 90 chars for Facebook)
      if (!formData.ogTitle && title) {
        updates.ogTitle =
          title.length > 90 ? title.substring(0, 87) + '...' : title;
      }

      // OG Description (max 200 chars)
      if (!formData.ogDescription) {
        const desc =
          excerpt || content.replace(/<[^>]*>/g, '').substring(0, 200);
        if (desc) {
          updates.ogDescription =
            desc.length > 200 ? desc.substring(0, 197) + '...' : desc;
        }
      }

      // Twitter Title (use OG Title or fallback)
      if (!formData.twitterTitle && (updates.ogTitle || formData.ogTitle)) {
        updates.twitterTitle = updates.ogTitle || formData.ogTitle;
      }

      // Twitter Description (use OG Description or fallback)
      if (
        !formData.twitterDescription &&
        (updates.ogDescription || formData.ogDescription)
      ) {
        updates.twitterDescription =
          updates.ogDescription || formData.ogDescription;
      }

      // OG and Twitter Images from featured image (for preview)
      if (featuredImage && !formData.ogImage) {
        const socialImages = generateSocialImages(featuredImage, title);
        updates.ogImage = socialImages.ogImage;
        updates.twitterImage = socialImages.twitterImage;
        setGeneratedOgImage(socialImages.ogImage);
        setGeneratedTwitterImage(socialImages.twitterImage);
      }

      // Canonical URL
      if (!formData.canonicalUrl && formData.slug) {
        updates.canonicalUrl = `https://wdhejbul.com/blog/${formData.slug}`;
      }

      // SEO Keywords from tags
      if (
        (!formData.seoKeywords || formData.seoKeywords.length === 0) &&
        tags.length > 0
      ) {
        updates.seoKeywords = tags.slice(0, 10);
      }

      setFormData(prev => ({ ...prev, ...updates }));
      setIsAutoFilling(false);
    },
    [formData, imagePreview],
  );

  // Validate SEO fields with detailed status
  const validateSEOFields = useCallback(() => {
    const validation = {
      seoTitle: { valid: false, message: '' },
      seoDescription: { valid: false, message: '' },
      seoKeywords: { valid: false, message: '' },
      ogTitle: { valid: false, message: '' },
      ogDescription: { valid: false, message: '' },
      ogImage: { valid: false, message: '' },
    };

    // SEO Title: 50-60 chars is optimal
    const titleLen = formData.seoTitle?.length || 0;
    if (titleLen === 0) {
      validation.seoTitle = { valid: false, message: '❌ Required for SEO' };
    } else if (titleLen < 50) {
      validation.seoTitle = {
        valid: false,
        message: `⚠️ ${titleLen}/60 - Add more content`,
      };
    } else if (titleLen <= 60) {
      validation.seoTitle = {
        valid: true,
        message: `✅ ${titleLen}/60 - Perfect length`,
      };
    } else {
      validation.seoTitle = {
        valid: false,
        message: `⚠️ ${titleLen}/60 - Too long (max 60)`,
      };
    }

    // SEO Description: 150-160 chars is optimal
    const descLen = formData.seoDescription?.length || 0;
    if (descLen === 0) {
      validation.seoDescription = {
        valid: false,
        message: '❌ Required for SEO',
      };
    } else if (descLen < 150) {
      validation.seoDescription = {
        valid: false,
        message: `⚠️ ${descLen}/160 - Add more content`,
      };
    } else if (descLen <= 160) {
      validation.seoDescription = {
        valid: true,
        message: `✅ ${descLen}/160 - Perfect length`,
      };
    } else {
      validation.seoDescription = {
        valid: false,
        message: `⚠️ ${descLen}/160 - Too long (max 160)`,
      };
    }

    // SEO Keywords: 5-10 keywords recommended
    const keywordCount = formData.seoKeywords?.length || 0;
    if (keywordCount === 0) {
      validation.seoKeywords = {
        valid: false,
        message: '❌ Add keywords for SEO',
      };
    } else if (keywordCount < 5) {
      validation.seoKeywords = {
        valid: false,
        message: `⚠️ ${keywordCount}/10 - Add more keywords`,
      };
    } else if (keywordCount <= 10) {
      validation.seoKeywords = {
        valid: true,
        message: `✅ ${keywordCount}/10 - Good keyword count`,
      };
    } else {
      validation.seoKeywords = {
        valid: false,
        message: `⚠️ ${keywordCount}/10 - Too many keywords (max 10)`,
      };
    }

    // OG Title: 60-90 chars is optimal
    const ogTitleLen = formData.ogTitle?.length || 0;
    if (ogTitleLen === 0) {
      validation.ogTitle = {
        valid: false,
        message: '❌ Required for social sharing',
      };
    } else if (ogTitleLen < 60) {
      validation.ogTitle = {
        valid: false,
        message: `⚠️ ${ogTitleLen}/90 - Add more content`,
      };
    } else if (ogTitleLen <= 90) {
      validation.ogTitle = {
        valid: true,
        message: `✅ ${ogTitleLen}/90 - Perfect length`,
      };
    } else {
      validation.ogTitle = {
        valid: false,
        message: `⚠️ ${ogTitleLen}/90 - Too long (max 90)`,
      };
    }

    // OG Description: 150-200 chars is optimal
    const ogDescLen = formData.ogDescription?.length || 0;
    if (ogDescLen === 0) {
      validation.ogDescription = {
        valid: false,
        message: '❌ Required for social sharing',
      };
    } else if (ogDescLen < 150) {
      validation.ogDescription = {
        valid: false,
        message: `⚠️ ${ogDescLen}/200 - Add more content`,
      };
    } else if (ogDescLen <= 200) {
      validation.ogDescription = {
        valid: true,
        message: `✅ ${ogDescLen}/200 - Perfect length`,
      };
    } else {
      validation.ogDescription = {
        valid: false,
        message: `⚠️ ${ogDescLen}/200 - Too long (max 200)`,
      };
    }

    // OG Image: Check if provided
    if (!formData.ogImage || formData.ogImage.length === 0) {
      validation.ogImage = {
        valid: false,
        message: '❌ Add OG image for social sharing',
      };
    } else {
      validation.ogImage = { valid: true, message: '✅ OG image provided' };
    }

    setSeoValidation(validation);
  }, [formData]);

  // Handle Auto-Fill button click
  const handleAutoFill = useCallback(() => {
    autoGenerateSocialFields({
      title: formData.title,
      excerpt: formData.excerpt,
      content: formData.content,
      tags: formData.tags,
      imagePreview: imagePreview,
      featuredImage: imagePreview,
    });
  }, [formData, imagePreview, autoGenerateSocialFields]);

  // Auto-save draft
  const autoSaveDraft = useCallback(() => {
    if (!formData.title && !formData.content) return;

    setIsAutoSaving(true);
    setShowAutoSaveIndicator(true);
    try {
      const draftData = {
        ...formData,
        editorPendingImages,
        imagePreview,
        savedAt: new Date().toISOString(),
      };
      localStorage.setItem('blog_draft', JSON.stringify(draftData));
      setLastSaved(new Date());
      setTimeout(() => setShowAutoSaveIndicator(false), 2000);
    } catch (error) {
      console.error('Auto-save failed:', error);
    } finally {
      setIsAutoSaving(false);
    }
  }, [formData, editorPendingImages, imagePreview]);

  useEffect(() => {
    if (autoSaveTimer) clearTimeout(autoSaveTimer);
    const timer = setTimeout(() => autoSaveDraft(), 3000);
    setAutoSaveTimer(timer);
    return () => clearTimeout(timer);
  }, [formData, editorPendingImages, imagePreview, autoSaveDraft]);

  // Handle image upload
  const handleImageUpload = (file, preview) => {
    setImageFile(file);
    setImagePreview(preview);

    if (preview) {
      const socialImages = generateSocialImages(preview, formData.title);
      setFormData(prev => ({
        ...prev,
        ogImage: socialImages.ogImage || prev.ogImage,
        twitterImage: socialImages.twitterImage || prev.twitterImage,
      }));
      setGeneratedOgImage(socialImages.ogImage);
      setGeneratedTwitterImage(socialImages.twitterImage);
    }
  };

  const handleSubmit = useCallback(async () => {
    // Validation
    if (!formData.title || !formData.title.trim()) {
      showToast('Title is required', 'error');
      return;
    }

    if (!formData.content || !formData.content.trim()) {
      showToast('Content is required', 'error');
      return;
    }

    if (!formData.category) {
      showToast('Category is required', 'error');
      return;
    }

    // Check pending images
    const pendingImages = (editorPendingImages || []).filter(
      img => img?.status === 'pending',
    );
    if (pendingImages.length > 0) {
      showToast('Please upload all pending images before saving', 'error');
      return;
    }

    const uploadingImages = (editorPendingImages || []).filter(
      img => img?.status === 'uploading',
    );
    if (uploadingImages.length > 0) {
      showToast('Images are still uploading. Please wait...', 'error');
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitError(null);

      // Auto-generate any missing social fields
      autoGenerateSocialFields({
        title: formData.title,
        excerpt: formData.excerpt,
        content: formData.content,
        tags: formData.tags,
        imagePreview: imagePreview,
        featuredImage: imagePreview,
      });

      // Prepare form data
      const formDataObj = new FormData();
      let cleanContent = formData.content;

      // Handle editor images - replace preview URLs with uploaded URLs
      const uploadedImagesList = (editorPendingImages || []).filter(
        img => img?.status === 'uploaded',
      );
      for (const img of uploadedImagesList) {
        if (img?.uploadedUrl && img?.previewUrl) {
          const escapedPreviewUrl = img.previewUrl.replace(
            /[.*+?^${}()|[\]\\]/g,
            '\\$&',
          );
          cleanContent = cleanContent.replace(
            new RegExp(escapedPreviewUrl, 'g'),
            img.uploadedUrl,
          );
        }
      }

      // Add content
      formDataObj.append('content', cleanContent);

      // Add all form fields (filter out undefined/empty values)
      const fieldsToSend = {
        title: formData.title.trim(),
        excerpt: formData.excerpt || '',
        category: formData.category,
        tags: formData.tags || [],
        status: formData.status || 'draft',
        featured: formData.featured || false,
        seoTitle: formData.seoTitle || '',
        seoDescription: formData.seoDescription || '',
        seoKeywords: formData.seoKeywords || [],
        canonicalUrl: formData.canonicalUrl || '',
        ogImage: formData.ogImage || '',
        ogTitle: formData.ogTitle || '',
        ogDescription: formData.ogDescription || '',
        twitterTitle: formData.twitterTitle || '',
        twitterDescription: formData.twitterDescription || '',
        twitterImage: formData.twitterImage || '',
        imageAlt: formData.imageAlt || formData.title || '',
        imageCaption: formData.imageCaption || '',
        slug: formData.slug || '',
      };

      // Only add scheduledAt if status is scheduled
      if (formData.status === 'scheduled' && formData.scheduledAt) {
        fieldsToSend.scheduledAt = formData.scheduledAt;
      }

      // Append all fields to FormData
      Object.entries(fieldsToSend).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value)) {
            if (value.length > 0) {
              formDataObj.append(key, JSON.stringify(value));
            }
          } else {
            formDataObj.append(key, String(value));
          }
        }
      });

      // Add featured image if changed
      if (imageFile) {
        formDataObj.append('featuredImage', imageFile);
      }

      console.log('📤 Submitting blog...', { mode, formData: fieldsToSend });

      // Call the submit function
      const result = await onSubmit(formDataObj);

      console.log('✅ Submit result:', result);

      // Check if the result indicates success
      if (result && result.success === false) {
        throw new Error(result.message || 'Failed to save blog');
      }

      // Close modal on success
      showToast(
        mode === 'create'
          ? 'Blog created successfully!'
          : 'Blog updated successfully!',
        'success',
      );

      // Close modal
      onClose();

      // Reset form after successful save
      resetForm();

      // Trigger refresh of blog list
      if (onRefresh) {
        console.log('🔄 Calling onRefresh...');
        setTimeout(() => {
          onRefresh();
        }, 500);
      } else {
        console.warn('⚠️ onRefresh not provided to BlogEditorModal');
      }
    } catch (error) {
      console.error('❌ Submit error:', error);
      const errorMessage =
        error.message || 'Failed to save blog. Please try again.';
      setSubmitError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  }, [
    formData,
    editorPendingImages,
    imageFile,
    imagePreview,
    onSubmit,
    onClose,
    onRefresh,
    mode,
    showToast,
    autoGenerateSocialFields,
    resetForm,
  ]);

  // Helper: Get status color for validation
  const getStatusColor = valid => {
    if (valid === true) return 'text-green-500';
    return 'text-yellow-500';
  };

  // Helper: Get status icon for validation
  const getStatusIcon = valid => {
    if (valid === true) return <FiCheck className="w-3 h-3 text-green-500" />;
    return <FiAlertCircle className="w-3 h-3 text-yellow-500" />;
  };

  if (!isOpen) return null;

  const isDark = resolvedTheme === 'dark';
  const isSubmittingState = isSubmitting || externalIsSubmitting;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm overflow-y-auto"
      style={{
        backgroundColor: isDark ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.5)',
      }}
    >
      <div
        className="relative w-full max-w-6xl my-4 rounded-2xl overflow-hidden shadow-2xl"
        style={{
          backgroundColor: isDark
            ? 'rgba(20, 20, 20, 0.95)'
            : 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          border: isDark
            ? '1px solid rgba(255, 255, 255, 0.08)'
            : '1px solid rgba(0, 0, 0, 0.06)',
          boxShadow: isDark
            ? '0 25px 50px -12px rgba(0, 0, 0, 0.8)'
            : '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        }}
      >
        {/* Header */}
        <div
          className="sticky top-0 z-10 px-4 py-3 border-b flex items-center justify-between"
          style={{
            borderColor: isDark
              ? 'rgba(255, 255, 255, 0.06)'
              : 'rgba(0, 0, 0, 0.06)',
            backgroundColor: isDark
              ? 'rgba(20, 20, 20, 0.8)'
              : 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <h2
            className="text-lg font-semibold"
            style={{ color: isDark ? '#ffffff' : '#0a0a0a' }}
          >
            {mode === 'create' ? 'Create New Blog Post' : 'Edit Blog Post'}
          </h2>
          <div className="flex items-center gap-2">
            {lastSaved && (
              <span
                className="text-xs flex items-center gap-1"
                style={{
                  color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                }}
              >
                <FiClock className="w-3 h-3" />
                {new Date(lastSaved).toLocaleTimeString()}
              </span>
            )}
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg transition-all duration-200 hover:bg-black/5 dark:hover:bg-white/5"
              style={{ color: isDark ? '#ffffff' : '#0a0a0a' }}
            >
              <FiXCircle className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Error Banner */}
        {submitError && (
          <div className="mx-4 mt-3 p-3 rounded-lg bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 text-sm flex items-center gap-2">
            <FiAlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{submitError}</span>
            <button
              onClick={() => setSubmitError(null)}
              className="ml-auto p-1 rounded hover:bg-red-200 dark:hover:bg-red-800/30"
            >
              <FiX className="w-3 h-3" />
            </button>
          </div>
        )}

        {/* Tabs */}
        <div
          className="flex gap-1.5 px-4 pt-3 pb-1.5 border-b overflow-x-auto"
          style={{
            borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
          }}
        >
          <button
            onClick={() => setActiveTab('editor')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
              activeTab === 'editor'
                ? 'bg-black/10 dark:bg-white/10'
                : 'hover:bg-black/5 dark:hover:bg-white/5'
            }`}
            style={{
              color:
                activeTab === 'editor'
                  ? isDark
                    ? '#ffffff'
                    : '#0a0a0a'
                  : isDark
                    ? 'rgba(255,255,255,0.5)'
                    : 'rgba(0,0,0,0.5)',
            }}
          >
            <FiEdit2 className="w-3.5 h-3.5 inline mr-1.5" />
            Editor
          </button>
          <button
            onClick={() => setActiveTab('seo')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
              activeTab === 'seo'
                ? 'bg-black/10 dark:bg-white/10'
                : 'hover:bg-black/5 dark:hover:bg-white/5'
            }`}
            style={{
              color:
                activeTab === 'seo'
                  ? isDark
                    ? '#ffffff'
                    : '#0a0a0a'
                  : isDark
                    ? 'rgba(255,255,255,0.5)'
                    : 'rgba(0,0,0,0.5)',
            }}
          >
            <FiTrendingUp className="w-3.5 h-3.5 inline mr-1.5" />
            SEO & Social
          </button>
          <button
            onClick={() => setActiveTab('social')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
              activeTab === 'social'
                ? 'bg-black/10 dark:bg-white/10'
                : 'hover:bg-black/5 dark:hover:bg-white/5'
            }`}
            style={{
              color:
                activeTab === 'social'
                  ? isDark
                    ? '#ffffff'
                    : '#0a0a0a'
                  : isDark
                    ? 'rgba(255,255,255,0.5)'
                    : 'rgba(0,0,0,0.5)',
            }}
          >
            <FiShare2 className="w-3.5 h-3.5 inline mr-1.5" />
            Social Preview
          </button>
          {mode === 'edit' && currentBlog && (
            <button
              onClick={() => setActiveTab('analytics')}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
                activeTab === 'analytics'
                  ? 'bg-black/10 dark:bg-white/10'
                  : 'hover:bg-black/5 dark:hover:bg-white/5'
              }`}
              style={{
                color:
                  activeTab === 'analytics'
                    ? isDark
                      ? '#ffffff'
                      : '#0a0a0a'
                    : isDark
                      ? 'rgba(255,255,255,0.5)'
                      : 'rgba(0,0,0,0.5)',
              }}
            >
              <FiBarChart2 className="w-3.5 h-3.5 inline mr-1.5" />
              Analytics
            </button>
          )}
        </div>

        {/* Tab Content */}
        <div className="p-4 max-h-[calc(100vh-180px)] overflow-y-auto">
          {activeTab === 'editor' && (
            <div className="space-y-4">
              {/* Auto-Fill Button */}
              <div className="flex items-center justify-end gap-2">
                <button
                  onClick={handleAutoFill}
                  disabled={isAutoFilling || !formData.title}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 hover:bg-black/5 dark:hover:bg-white/5 flex items-center gap-1.5 disabled:opacity-50"
                  style={{
                    borderColor: isDark
                      ? 'rgba(255,255,255,0.1)'
                      : 'rgba(0,0,0,0.1)',
                    color: isDark ? '#ffffff' : '#0a0a0a',
                    border: '1px solid',
                  }}
                >
                  {isAutoFilling ? (
                    <>
                      <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <FiZap className="w-3 h-3" />
                      Auto-Fill Social Fields
                    </>
                  )}
                </button>
              </div>

              {/* Image Status Indicator */}
              {editorPendingImages.length > 0 && (
                <div
                  className="p-2 rounded-lg border text-xs"
                  style={{
                    borderColor: isDark
                      ? 'rgba(255,255,255,0.06)'
                      : 'rgba(0,0,0,0.06)',
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FiImage
                        className="w-3.5 h-3.5"
                        style={{
                          color: isDark
                            ? 'rgba(255,255,255,0.4)'
                            : 'rgba(0,0,0,0.4)',
                        }}
                      />
                      <span
                        className="font-medium"
                        style={{ color: isDark ? '#ffffff' : '#0a0a0a' }}
                      >
                        Image Status
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-[10px]">
                      {editorPendingImages.filter(
                        img => img.status === 'pending',
                      ).length > 0 && (
                        <span className="text-yellow-500 flex items-center gap-1">
                          <FiAlertCircle className="w-3 h-3" />
                          {
                            editorPendingImages.filter(
                              img => img.status === 'pending',
                            ).length
                          }{' '}
                          pending
                        </span>
                      )}
                      {editorPendingImages.filter(
                        img => img.status === 'uploading',
                      ).length > 0 && (
                        <span className="text-blue-500 flex items-center gap-1">
                          <div className="animate-spin rounded-full h-2.5 w-2.5 border-2 border-blue-500 border-t-transparent" />
                          {
                            editorPendingImages.filter(
                              img => img.status === 'uploading',
                            ).length
                          }{' '}
                          uploading
                        </span>
                      )}
                      {editorPendingImages.filter(
                        img => img.status === 'uploaded',
                      ).length > 0 && (
                        <span className="text-green-500 flex items-center gap-1">
                          <FiCheckCircle className="w-3 h-3" />
                          {
                            editorPendingImages.filter(
                              img => img.status === 'uploaded',
                            ).length
                          }{' '}
                          uploaded
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Title */}
              <div>
                <label
                  className="block text-xs font-medium mb-1.5"
                  style={{ color: isDark ? '#ffffff' : '#0a0a0a' }}
                >
                  Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={e =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  className="w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-black/20 dark:focus:ring-white/20 bg-transparent transition-all text-sm"
                  style={{
                    borderColor: isDark
                      ? 'rgba(255,255,255,0.1)'
                      : 'rgba(0,0,0,0.1)',
                    color: isDark ? '#ffffff' : '#0a0a0a',
                  }}
                  placeholder="Enter blog title"
                  maxLength="200"
                />
                <p
                  className="text-[10px] mt-1"
                  style={{
                    color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                  }}
                >
                  {formData.title.length}/200 characters
                </p>
              </div>

              {/* Slug */}
              <div>
                <label
                  className="block text-xs font-medium mb-1.5"
                  style={{ color: isDark ? '#ffffff' : '#0a0a0a' }}
                >
                  Slug
                </label>
                <SlugGenerator
                  title={formData.title}
                  onSlugChange={slug => setFormData({ ...formData, slug })}
                  existingSlug={formData.slug}
                />
              </div>

              {/* Featured Image */}
              <div>
                <label
                  className="block text-xs font-medium mb-1.5"
                  style={{ color: isDark ? '#ffffff' : '#0a0a0a' }}
                >
                  Featured Image
                </label>
                <ImageUploader
                  onImageUpload={(file, preview) => {
                    setImageFile(file);
                    setImagePreview(preview);
                  }}
                  existingImage={imagePreview}
                  onRemoveImage={() => {
                    setImageFile(null);
                    setImagePreview('');
                  }}
                  showMetadata={true}
                  showCropGuide={true}
                  showRatioSelector={true}
                />
              </div>

              {/* Category & Tags */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label
                    className="block text-xs font-medium mb-1.5"
                    style={{ color: isDark ? '#ffffff' : '#0a0a0a' }}
                  >
                    Category *
                  </label>
                  <select
                    value={formData.category}
                    onChange={e =>
                      setFormData({ ...formData, category: e.target.value })
                    }
                    className="w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-black/20 dark:focus:ring-white/20 bg-transparent transition-all text-sm"
                    style={{
                      borderColor: isDark
                        ? 'rgba(255,255,255,0.1)'
                        : 'rgba(0,0,0,0.1)',
                      color: isDark ? '#ffffff' : '#0a0a0a',
                    }}
                  >
                    <option value="">Select Category</option>
                    {categories.map(cat => (
                      <option key={cat.name} value={cat.name}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                  <CategoryManager
                    categories={categories}
                    onCategoriesUpdate={onCategoriesUpdate}
                  />
                </div>
                <div>
                  <label
                    className="block text-xs font-medium mb-1.5"
                    style={{ color: isDark ? '#ffffff' : '#0a0a0a' }}
                  >
                    Tags
                  </label>
                  <TagManager
                    selectedTags={formData.tags}
                    onTagsChange={tags => setFormData({ ...formData, tags })}
                    existingTags={tags.map(t => t.name)}
                  />
                </div>
              </div>

              {/* Excerpt */}
              <div>
                <label
                  className="block text-xs font-medium mb-1.5"
                  style={{ color: isDark ? '#ffffff' : '#0a0a0a' }}
                >
                  Excerpt
                </label>
                <textarea
                  value={formData.excerpt}
                  onChange={e =>
                    setFormData({ ...formData, excerpt: e.target.value })
                  }
                  rows={2}
                  className="w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-black/20 dark:focus:ring-white/20 bg-transparent transition-all text-sm"
                  style={{
                    borderColor: isDark
                      ? 'rgba(255,255,255,0.1)'
                      : 'rgba(0,0,0,0.1)',
                    color: isDark ? '#ffffff' : '#0a0a0a',
                  }}
                  placeholder="Brief summary of the blog post"
                  maxLength="160"
                />
                <p
                  className="text-[10px] mt-1"
                  style={{
                    color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                  }}
                >
                  {formData.excerpt.length} / 160 characters recommended
                </p>
              </div>

              {/* Content */}
              <div>
                <label
                  className="block text-xs font-medium mb-1.5"
                  style={{ color: isDark ? '#ffffff' : '#0a0a0a' }}
                >
                  Content *
                </label>
                <BlogEditor
                  content={formData.content}
                  onChange={content => setFormData({ ...formData, content })}
                  onImagesChange={images => {
                    setEditorPendingImages(images);
                    const hasPending = images.some(
                      img => img.status === 'pending',
                    );
                    const hasUploading = images.some(
                      img => img.status === 'uploading',
                    );
                    setImagesReadyForSubmit(!hasPending && !hasUploading);
                  }}
                  placeholder="Write your blog content here..."
                />
              </div>

              {/* Status & Settings */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label
                    className="block text-xs font-medium mb-1.5"
                    style={{ color: isDark ? '#ffffff' : '#0a0a0a' }}
                  >
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={e =>
                      setFormData({ ...formData, status: e.target.value })
                    }
                    className="w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-black/20 dark:focus:ring-white/20 bg-transparent transition-all text-sm"
                    style={{
                      borderColor: isDark
                        ? 'rgba(255,255,255,0.1)'
                        : 'rgba(0,0,0,0.1)',
                      color: isDark ? '#ffffff' : '#0a0a0a',
                    }}
                  >
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                    <option value="scheduled">Scheduled</option>
                  </select>
                </div>
                {formData.status === 'scheduled' && (
                  <div>
                    <label
                      className="block text-xs font-medium mb-1.5"
                      style={{ color: isDark ? '#ffffff' : '#0a0a0a' }}
                    >
                      Schedule Date
                    </label>
                    <SchedulePicker
                      value={formData.scheduledAt}
                      onChange={date =>
                        setFormData({ ...formData, scheduledAt: date })
                      }
                    />
                  </div>
                )}
                <div>
                  <label className="flex items-center gap-2 mt-6">
                    <input
                      type="checkbox"
                      checked={formData.featured}
                      onChange={e =>
                        setFormData({ ...formData, featured: e.target.checked })
                      }
                      className="rounded border-gray-300 dark:border-gray-600"
                    />
                    <span
                      className="text-xs"
                      style={{ color: isDark ? '#ffffff' : '#0a0a0a' }}
                    >
                      Feature this post
                    </span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'seo' && (
            <div className="space-y-4">
              {/* SEO Score */}
              <div className="mb-3">
                <SEOScoreHelper
                  title={formData.seoTitle || formData.title}
                  description={formData.seoDescription || formData.excerpt}
                  keywords={formData.seoKeywords}
                />
              </div>

              {/* Auto-generate button */}
              <div className="flex items-center justify-end gap-2">
                <button
                  onClick={handleAutoFill}
                  disabled={isAutoFilling || !formData.title}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 hover:bg-black/5 dark:hover:bg-white/5 flex items-center gap-1.5 disabled:opacity-50"
                  style={{
                    borderColor: isDark
                      ? 'rgba(255,255,255,0.1)'
                      : 'rgba(0,0,0,0.1)',
                    color: isDark ? '#ffffff' : '#0a0a0a',
                    border: '1px solid',
                  }}
                >
                  {isAutoFilling ? (
                    <>
                      <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <FiZap className="w-3 h-3" />
                      Auto-Fill All Social Fields
                    </>
                  )}
                </button>
              </div>

              {/* SEO Meta Tags */}
              <div
                className="border-t pt-3"
                style={{
                  borderColor: isDark
                    ? 'rgba(255,255,255,0.06)'
                    : 'rgba(0,0,0,0.06)',
                }}
              >
                <h3
                  className="text-sm font-semibold mb-3 flex items-center gap-1.5"
                  style={{ color: isDark ? '#ffffff' : '#0a0a0a' }}
                >
                  <FiTrendingUp className="w-4 h-4" />
                  SEO Meta Tags
                  <span
                    className="text-[10px] font-normal ml-2"
                    style={{
                      color: isDark
                        ? 'rgba(255,255,255,0.4)'
                        : 'rgba(0,0,0,0.4)',
                    }}
                  >
                    (All fields auto-validate)
                  </span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {/* SEO Title */}
                  <div>
                    <label
                      className="block text-xs font-medium mb-1.5 flex items-center justify-between"
                      style={{ color: isDark ? '#ffffff' : '#0a0a0a' }}
                    >
                      <span>SEO Title</span>
                      <span className="flex items-center gap-1 text-[10px]">
                        {getStatusIcon(seoValidation.seoTitle.valid)}
                        <span
                          className={getStatusColor(
                            seoValidation.seoTitle.valid,
                          )}
                        >
                          {seoValidation.seoTitle.message}
                        </span>
                      </span>
                    </label>
                    <input
                      type="text"
                      value={formData.seoTitle}
                      onChange={e =>
                        setFormData({ ...formData, seoTitle: e.target.value })
                      }
                      className="w-full px-3 py-1.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-black/20 dark:focus:ring-white/20 bg-transparent transition-all text-sm"
                      style={{
                        borderColor: seoValidation.seoTitle.valid
                          ? isDark
                            ? 'rgba(34,197,94,0.3)'
                            : 'rgba(34,197,94,0.3)'
                          : isDark
                            ? 'rgba(234,179,8,0.3)'
                            : 'rgba(234,179,8,0.3)',
                        color: isDark ? '#ffffff' : '#0a0a0a',
                      }}
                      placeholder="SEO Title (50-60 chars recommended)"
                      maxLength="60"
                    />
                    <div className="flex justify-between mt-0.5">
                      <span
                        className="text-[10px]"
                        style={{
                          color: isDark
                            ? 'rgba(255,255,255,0.3)'
                            : 'rgba(0,0,0,0.3)',
                        }}
                      >
                        {formData.seoTitle.length}/60 chars
                      </span>
                      <span
                        className="text-[10px]"
                        style={{
                          color:
                            formData.seoTitle.length > 60
                              ? '#ef4444'
                              : formData.seoTitle.length > 50
                                ? '#22c55e'
                                : '#f59e0b',
                        }}
                      >
                        {formData.seoTitle.length >= 50 &&
                        formData.seoTitle.length <= 60
                          ? '✅ Optimal'
                          : formData.seoTitle.length > 60
                            ? '⚠️ Too long'
                            : '⚠️ Too short'}
                      </span>
                    </div>
                  </div>

                  {/* SEO Description */}
                  <div>
                    <label
                      className="block text-xs font-medium mb-1.5 flex items-center justify-between"
                      style={{ color: isDark ? '#ffffff' : '#0a0a0a' }}
                    >
                      <span>SEO Description</span>
                      <span className="flex items-center gap-1 text-[10px]">
                        {getStatusIcon(seoValidation.seoDescription.valid)}
                        <span
                          className={getStatusColor(
                            seoValidation.seoDescription.valid,
                          )}
                        >
                          {seoValidation.seoDescription.message}
                        </span>
                      </span>
                    </label>
                    <textarea
                      value={formData.seoDescription}
                      onChange={e =>
                        setFormData({
                          ...formData,
                          seoDescription: e.target.value,
                        })
                      }
                      rows={2}
                      className="w-full px-3 py-1.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-black/20 dark:focus:ring-white/20 bg-transparent transition-all text-sm"
                      style={{
                        borderColor: seoValidation.seoDescription.valid
                          ? isDark
                            ? 'rgba(34,197,94,0.3)'
                            : 'rgba(34,197,94,0.3)'
                          : isDark
                            ? 'rgba(234,179,8,0.3)'
                            : 'rgba(234,179,8,0.3)',
                        color: isDark ? '#ffffff' : '#0a0a0a',
                      }}
                      placeholder="SEO Description (150-160 chars recommended)"
                      maxLength="160"
                    />
                    <div className="flex justify-between mt-0.5">
                      <span
                        className="text-[10px]"
                        style={{
                          color: isDark
                            ? 'rgba(255,255,255,0.3)'
                            : 'rgba(0,0,0,0.3)',
                        }}
                      >
                        {formData.seoDescription.length}/160 chars
                      </span>
                      <span
                        className="text-[10px]"
                        style={{
                          color:
                            formData.seoDescription.length > 160
                              ? '#ef4444'
                              : formData.seoDescription.length > 150
                                ? '#22c55e'
                                : '#f59e0b',
                        }}
                      >
                        {formData.seoDescription.length >= 150 &&
                        formData.seoDescription.length <= 160
                          ? '✅ Optimal'
                          : formData.seoDescription.length > 160
                            ? '⚠️ Too long'
                            : '⚠️ Too short'}
                      </span>
                    </div>
                  </div>

                  {/* SEO Keywords */}
                  <div>
                    <label
                      className="block text-xs font-medium mb-1.5 flex items-center justify-between"
                      style={{ color: isDark ? '#ffffff' : '#0a0a0a' }}
                    >
                      <span>SEO Keywords</span>
                      <span className="flex items-center gap-1 text-[10px]">
                        {getStatusIcon(seoValidation.seoKeywords.valid)}
                        <span
                          className={getStatusColor(
                            seoValidation.seoKeywords.valid,
                          )}
                        >
                          {seoValidation.seoKeywords.message}
                        </span>
                      </span>
                    </label>
                    <input
                      type="text"
                      value={formData.seoKeywords.join(', ')}
                      onChange={e =>
                        setFormData({
                          ...formData,
                          seoKeywords: e.target.value
                            .split(',')
                            .map(k => k.trim())
                            .filter(k => k),
                        })
                      }
                      className="w-full px-3 py-1.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-black/20 dark:focus:ring-white/20 bg-transparent transition-all text-sm"
                      style={{
                        borderColor: seoValidation.seoKeywords.valid
                          ? isDark
                            ? 'rgba(34,197,94,0.3)'
                            : 'rgba(34,197,94,0.3)'
                          : isDark
                            ? 'rgba(234,179,8,0.3)'
                            : 'rgba(234,179,8,0.3)',
                        color: isDark ? '#ffffff' : '#0a0a0a',
                      }}
                      placeholder="keyword1, keyword2, keyword3"
                    />
                    <p
                      className="text-[10px] mt-0.5"
                      style={{
                        color: isDark
                          ? 'rgba(255,255,255,0.3)'
                          : 'rgba(0,0,0,0.3)',
                      }}
                    >
                      {formData.seoKeywords.length} / 10 keywords recommended
                    </p>
                  </div>

                  {/* Canonical URL */}
                  <div>
                    <label
                      className="block text-xs font-medium mb-1.5 flex items-center gap-1.5"
                      style={{ color: isDark ? '#ffffff' : '#0a0a0a' }}
                    >
                      <FiLink className="w-3 h-3" />
                      Canonical URL
                    </label>
                    <input
                      type="text"
                      value={formData.canonicalUrl}
                      onChange={e =>
                        setFormData({
                          ...formData,
                          canonicalUrl: e.target.value,
                        })
                      }
                      className="w-full px-3 py-1.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-black/20 dark:focus:ring-white/20 bg-transparent transition-all text-sm"
                      style={{
                        borderColor: isDark
                          ? 'rgba(255,255,255,0.1)'
                          : 'rgba(0,0,0,0.1)',
                        color: isDark ? '#ffffff' : '#0a0a0a',
                      }}
                      placeholder="https://wd-hejbul.vercel.app/blog/your-post"
                    />
                    {formData.slug && (
                      <p
                        className="text-[10px] mt-0.5"
                        style={{
                          color: isDark
                            ? 'rgba(255,255,255,0.3)'
                            : 'rgba(0,0,0,0.3)',
                        }}
                      >
                        Suggested: https://wdhejbul.com/blog/
                        {formData.slug}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Open Graph */}
              <div
                className="border-t pt-3"
                style={{
                  borderColor: isDark
                    ? 'rgba(255,255,255,0.06)'
                    : 'rgba(0,0,0,0.06)',
                }}
              >
                <h3
                  className="text-sm font-semibold mb-3 flex items-center gap-1.5"
                  style={{ color: isDark ? '#ffffff' : '#0a0a0a' }}
                >
                  <FiFacebook className="w-4 h-4" />
                  Open Graph (Facebook, LinkedIn)
                  <span
                    className="text-[10px] font-normal ml-2"
                    style={{
                      color: isDark
                        ? 'rgba(255,255,255,0.4)'
                        : 'rgba(0,0,0,0.4)',
                    }}
                  >
                    (Auto-validated)
                  </span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {/* OG Image */}
                  <div>
                    <label
                      className="block text-xs font-medium mb-1.5 flex items-center justify-between"
                      style={{ color: isDark ? '#ffffff' : '#0a0a0a' }}
                    >
                      <span>OG Image</span>
                      <span className="flex items-center gap-1 text-[10px]">
                        {getStatusIcon(seoValidation.ogImage.valid)}
                        <span
                          className={getStatusColor(
                            seoValidation.ogImage.valid,
                          )}
                        >
                          {seoValidation.ogImage.message}
                        </span>
                      </span>
                    </label>
                    <input
                      type="text"
                      value={formData.ogImage}
                      onChange={e =>
                        setFormData({ ...formData, ogImage: e.target.value })
                      }
                      className="w-full px-3 py-1.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-black/20 dark:focus:ring-white/20 bg-transparent transition-all text-sm"
                      style={{
                        borderColor: seoValidation.ogImage.valid
                          ? isDark
                            ? 'rgba(34,197,94,0.3)'
                            : 'rgba(34,197,94,0.3)'
                          : isDark
                            ? 'rgba(234,179,8,0.3)'
                            : 'rgba(234,179,8,0.3)',
                        color: isDark ? '#ffffff' : '#0a0a0a',
                      }}
                      placeholder="https://res.cloudinary.com/..."
                    />
                    {formData.ogImage && (
                      <div
                        className="mt-1.5 w-full h-24 rounded-lg overflow-hidden border"
                        style={{
                          borderColor: isDark
                            ? 'rgba(255,255,255,0.1)'
                            : 'rgba(0,0,0,0.1)',
                        }}
                      >
                        <img
                          src={formData.ogImage}
                          alt="OG Preview"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    {generatedOgImage && (
                      <button
                        onClick={() =>
                          setFormData({
                            ...formData,
                            ogImage: generatedOgImage,
                          })
                        }
                        className="text-[10px] mt-1 text-blue-500 hover:underline flex items-center gap-1"
                      >
                        <FiCopy className="w-3 h-3" />
                        Use auto-generated image
                      </button>
                    )}
                  </div>

                  {/* OG Title */}
                  <div>
                    <label
                      className="block text-xs font-medium mb-1.5 flex items-center justify-between"
                      style={{ color: isDark ? '#ffffff' : '#0a0a0a' }}
                    >
                      <span>OG Title</span>
                      <span className="flex items-center gap-1 text-[10px]">
                        {getStatusIcon(seoValidation.ogTitle.valid)}
                        <span
                          className={getStatusColor(
                            seoValidation.ogTitle.valid,
                          )}
                        >
                          {seoValidation.ogTitle.message}
                        </span>
                      </span>
                    </label>
                    <input
                      type="text"
                      value={formData.ogTitle}
                      onChange={e =>
                        setFormData({ ...formData, ogTitle: e.target.value })
                      }
                      className="w-full px-3 py-1.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-black/20 dark:focus:ring-white/20 bg-transparent transition-all text-sm"
                      style={{
                        borderColor: seoValidation.ogTitle.valid
                          ? isDark
                            ? 'rgba(34,197,94,0.3)'
                            : 'rgba(34,197,94,0.3)'
                          : isDark
                            ? 'rgba(234,179,8,0.3)'
                            : 'rgba(234,179,8,0.3)',
                        color: isDark ? '#ffffff' : '#0a0a0a',
                      }}
                      placeholder="OG Title (60-90 chars recommended)"
                      maxLength="95"
                    />
                    <div className="flex justify-between mt-0.5">
                      <span
                        className="text-[10px]"
                        style={{
                          color: isDark
                            ? 'rgba(255,255,255,0.3)'
                            : 'rgba(0,0,0,0.3)',
                        }}
                      >
                        {formData.ogTitle.length}/95 chars
                      </span>
                      <span
                        className="text-[10px]"
                        style={{
                          color:
                            formData.ogTitle.length > 95
                              ? '#ef4444'
                              : formData.ogTitle.length > 60
                                ? '#22c55e'
                                : '#f59e0b',
                        }}
                      >
                        {formData.ogTitle.length >= 60 &&
                        formData.ogTitle.length <= 90
                          ? '✅ Optimal'
                          : formData.ogTitle.length > 90
                            ? '⚠️ Long'
                            : '⚠️ Short'}
                      </span>
                    </div>
                  </div>

                  {/* OG Description */}
                  <div className="md:col-span-2">
                    <label
                      className="block text-xs font-medium mb-1.5 flex items-center justify-between"
                      style={{ color: isDark ? '#ffffff' : '#0a0a0a' }}
                    >
                      <span>OG Description</span>
                      <span className="flex items-center gap-1 text-[10px]">
                        {getStatusIcon(seoValidation.ogDescription.valid)}
                        <span
                          className={getStatusColor(
                            seoValidation.ogDescription.valid,
                          )}
                        >
                          {seoValidation.ogDescription.message}
                        </span>
                      </span>
                    </label>
                    <textarea
                      value={formData.ogDescription}
                      onChange={e =>
                        setFormData({
                          ...formData,
                          ogDescription: e.target.value,
                        })
                      }
                      rows={2}
                      className="w-full px-3 py-1.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-black/20 dark:focus:ring-white/20 bg-transparent transition-all text-sm"
                      style={{
                        borderColor: seoValidation.ogDescription.valid
                          ? isDark
                            ? 'rgba(34,197,94,0.3)'
                            : 'rgba(34,197,94,0.3)'
                          : isDark
                            ? 'rgba(234,179,8,0.3)'
                            : 'rgba(234,179,8,0.3)',
                        color: isDark ? '#ffffff' : '#0a0a0a',
                      }}
                      placeholder="OG Description (150-200 chars recommended)"
                      maxLength="200"
                    />
                    <div className="flex justify-between mt-0.5">
                      <span
                        className="text-[10px]"
                        style={{
                          color: isDark
                            ? 'rgba(255,255,255,0.3)'
                            : 'rgba(0,0,0,0.3)',
                        }}
                      >
                        {formData.ogDescription.length}/200 chars
                      </span>
                      <span
                        className="text-[10px]"
                        style={{
                          color:
                            formData.ogDescription.length > 200
                              ? '#ef4444'
                              : formData.ogDescription.length > 150
                                ? '#22c55e'
                                : '#f59e0b',
                        }}
                      >
                        {formData.ogDescription.length >= 150 &&
                        formData.ogDescription.length <= 200
                          ? '✅ Optimal'
                          : formData.ogDescription.length > 200
                            ? '⚠️ Too long'
                            : '⚠️ Too short'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Twitter Card */}
              <div
                className="border-t pt-3"
                style={{
                  borderColor: isDark
                    ? 'rgba(255,255,255,0.06)'
                    : 'rgba(0,0,0,0.06)',
                }}
              >
                <h3
                  className="text-sm font-semibold mb-3 flex items-center gap-1.5"
                  style={{ color: isDark ? '#ffffff' : '#0a0a0a' }}
                >
                  <FiTwitter className="w-4 h-4" />
                  Twitter Card
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label
                      className="block text-xs font-medium mb-1.5"
                      style={{ color: isDark ? '#ffffff' : '#0a0a0a' }}
                    >
                      Twitter Image{' '}
                      {generatedTwitterImage && (
                        <span className="text-[10px] text-green-500 ml-1">
                          ✓ Auto
                        </span>
                      )}
                    </label>
                    <input
                      type="text"
                      value={formData.twitterImage}
                      onChange={e =>
                        setFormData({
                          ...formData,
                          twitterImage: e.target.value,
                        })
                      }
                      className="w-full px-3 py-1.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-black/20 dark:focus:ring-white/20 bg-transparent transition-all text-sm"
                      style={{
                        borderColor: isDark
                          ? 'rgba(255,255,255,0.1)'
                          : 'rgba(0,0,0,0.1)',
                        color: isDark ? '#ffffff' : '#0a0a0a',
                      }}
                      placeholder="https://res.cloudinary.com/..."
                    />
                    {formData.twitterImage && (
                      <div
                        className="mt-1.5 w-full h-24 rounded-lg overflow-hidden border"
                        style={{
                          borderColor: isDark
                            ? 'rgba(255,255,255,0.1)'
                            : 'rgba(0,0,0,0.1)',
                        }}
                      >
                        <img
                          src={formData.twitterImage}
                          alt="Twitter Preview"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    {generatedTwitterImage && (
                      <button
                        onClick={() =>
                          setFormData({
                            ...formData,
                            twitterImage: generatedTwitterImage,
                          })
                        }
                        className="text-[10px] mt-1 text-blue-500 hover:underline flex items-center gap-1"
                      >
                        <FiCopy className="w-3 h-3" />
                        Use auto-generated image
                      </button>
                    )}
                  </div>
                  <div>
                    <label
                      className="block text-xs font-medium mb-1.5"
                      style={{ color: isDark ? '#ffffff' : '#0a0a0a' }}
                    >
                      Twitter Title{' '}
                      {formData.twitterTitle && (
                        <span className="text-[10px] text-green-500 ml-1">
                          ✓ Auto
                        </span>
                      )}
                    </label>
                    <input
                      type="text"
                      value={formData.twitterTitle}
                      onChange={e =>
                        setFormData({
                          ...formData,
                          twitterTitle: e.target.value,
                        })
                      }
                      className="w-full px-3 py-1.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-black/20 dark:focus:ring-white/20 bg-transparent transition-all text-sm"
                      style={{
                        borderColor: isDark
                          ? 'rgba(255,255,255,0.1)'
                          : 'rgba(0,0,0,0.1)',
                        color: isDark ? '#ffffff' : '#0a0a0a',
                      }}
                      placeholder="Twitter Title (60-90 chars recommended)"
                      maxLength="95"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label
                      className="block text-xs font-medium mb-1.5"
                      style={{ color: isDark ? '#ffffff' : '#0a0a0a' }}
                    >
                      Twitter Description{' '}
                      {formData.twitterDescription && (
                        <span className="text-[10px] text-green-500 ml-1">
                          ✓ Auto
                        </span>
                      )}
                    </label>
                    <textarea
                      value={formData.twitterDescription}
                      onChange={e =>
                        setFormData({
                          ...formData,
                          twitterDescription: e.target.value,
                        })
                      }
                      rows={2}
                      className="w-full px-3 py-1.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-black/20 dark:focus:ring-white/20 bg-transparent transition-all text-sm"
                      style={{
                        borderColor: isDark
                          ? 'rgba(255,255,255,0.1)'
                          : 'rgba(0,0,0,0.1)',
                        color: isDark ? '#ffffff' : '#0a0a0a',
                      }}
                      placeholder="Twitter Description (150-200 chars recommended)"
                      maxLength="200"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'social' && (
            <div className="space-y-4">
              <SocialSharingPreview
                blogData={formData}
                imagePreview={imagePreview}
              />
            </div>
          )}

          {activeTab === 'analytics' && currentBlog && (
            <div className="max-h-[60vh] overflow-y-auto">
              <BlogAnalytics blogId={currentBlog.id} />
            </div>
          )}
        </div>

        {/* Footer */}
        {activeTab !== 'analytics' && (
          <div
            className="sticky bottom-0 px-4 py-3 border-t flex items-center justify-end gap-2.5"
            style={{
              borderColor: isDark
                ? 'rgba(255,255,255,0.06)'
                : 'rgba(0,0,0,0.06)',
              backgroundColor: isDark
                ? 'rgba(20, 20, 20, 0.8)'
                : 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
            }}
          >
            <button
              onClick={onClose}
              disabled={isSubmittingState}
              className="px-4 py-1.5 rounded-lg border transition-all duration-200 hover:bg-black/5 dark:hover:bg-white/5 text-sm disabled:opacity-50"
              style={{
                borderColor: isDark
                  ? 'rgba(255,255,255,0.1)'
                  : 'rgba(0,0,0,0.1)',
                color: isDark ? '#ffffff' : '#0a0a0a',
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmittingState}
              className="px-5 py-1.5 rounded-lg font-medium transition-all duration-200 hover:opacity-90 disabled:opacity-50 flex items-center gap-2 text-sm"
              style={{
                backgroundColor: isDark ? '#ffffff' : '#0a0a0a',
                color: isDark ? '#0a0a0a' : '#ffffff',
              }}
            >
              {isSubmittingState ? (
                <>
                  <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-current border-t-transparent" />
                  Saving...
                </>
              ) : mode === 'create' ? (
                <>
                  <FiUploadCloud className="w-3.5 h-3.5" />
                  Create Post
                </>
              ) : (
                <>
                  <FiSave className="w-3.5 h-3.5" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogEditorModal;
